import { useState, useCallback, useEffect } from 'react';
import type { WeatherAlert } from '@/types/headerFeed';

const ALERT_STORAGE_KEY = 'geniebot-weather-alerts-shown';
const NOTIFICATION_PERMISSION_KEY = 'geniebot-weather-notifications';

export function useWeatherNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isEnabled, setIsEnabled] = useState(() => {
    return localStorage.getItem(NOTIFICATION_PERMISSION_KEY) === 'enabled';
  });

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        localStorage.setItem(NOTIFICATION_PERMISSION_KEY, 'enabled');
        setIsEnabled(true);
        return true;
      }
      return false;
    } catch (e) {
      console.error('Permission request failed:', e);
      return false;
    }
  }, []);

  const disableNotifications = useCallback(() => {
    localStorage.removeItem(NOTIFICATION_PERMISSION_KEY);
    setIsEnabled(false);
  }, []);

  const showNotification = useCallback((title: string, options?: NotificationOptions) => {
    if (permission !== 'granted' || !isEnabled) return;

    // Use service worker if available
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, {
          icon: '/favicon.png',
          badge: '/favicon.png',
          ...options,
        });
      });
    } else if ('Notification' in window) {
      new Notification(title, {
        icon: '/favicon.png',
        ...options,
      });
    }
  }, [permission, isEnabled]);

  const checkAndNotifyAlerts = useCallback((alerts: WeatherAlert[] | undefined) => {
    if (!alerts || alerts.length === 0 || !isEnabled || permission !== 'granted') return;

    // Get previously shown alerts
    const shownAlerts = JSON.parse(localStorage.getItem(ALERT_STORAGE_KEY) || '{}');
    const now = Date.now();
    
    // Clean old entries (older than 24 hours)
    for (const [key, timestamp] of Object.entries(shownAlerts)) {
      if (now - (timestamp as number) > 24 * 60 * 60 * 1000) {
        delete shownAlerts[key];
      }
    }

    // Only notify for severe/extreme alerts that haven't been shown
    const severeAlerts = alerts.filter(
      a => (a.severity === 'severe' || a.severity === 'extreme') && 
           !shownAlerts[a.event + a.start]
    );

    if (severeAlerts.length > 0) {
      const alert = severeAlerts[0];
      const alertKey = alert.event + alert.start;
      
      showNotification(`⚠️ ${alert.event}`, {
        body: alert.headline || `${alert.severity.toUpperCase()} weather alert in your area`,
        tag: `weather-${alertKey}`,
        requireInteraction: alert.severity === 'extreme',
      });

      // Mark as shown
      shownAlerts[alertKey] = now;
      localStorage.setItem(ALERT_STORAGE_KEY, JSON.stringify(shownAlerts));
    }
  }, [isEnabled, permission, showNotification]);

  return {
    permission,
    isEnabled,
    requestPermission,
    disableNotifications,
    showNotification,
    checkAndNotifyAlerts,
    isSupported: 'Notification' in window,
  };
}
